'use client';

import React from 'react';

import { Badge } from './Badge';
import { Button } from './Button';
import { Skeleton } from './Skeleton';

export type SaldoCicloState =
  | 'normal'
  | 'atencao'
  | 'esgotado'
  | 'trial'
  | 'ilimitado'
  | 'semFranquia'
  | 'semPlano'
  | 'loading'
  | 'erro';

export interface SaldoCicloMetricaSecundaria {
  label: string;
  used: number;
  total: number;
  state: SaldoCicloState;
  paymentMode?: 'prepaid' | 'postpaid';
}

export interface SaldoCicloCardProps {
  productName: string;
  planLabel?: string;
  state: SaldoCicloState;
  showLabel?: boolean;
  used?: number;
  total?: number | null;
  primaryLabel?: string;
  secondaryMetric?: SaldoCicloMetricaSecundaria;
  infoLabel?: string;
  paymentMode?: 'prepaid' | 'postpaid';
  overageCount?: number;
  trialEndingSoon?: boolean;
  onRenovar?: () => void;
  onVerPlano?: () => void;
  verPlanoLabel?: string;
  className?: string;
}

const FILL_CLASS: Record<string, string> = {
  normal: 'bg-brand-600',
  trial: 'bg-brand-600',
  atencao: 'bg-warning-600',
  esgotado: 'bg-error-600',
};

const formatNumber = (value: number): string => value.toLocaleString('pt-BR');

const SaldoBar = ({
  used,
  total,
  state,
  totalSuffix,
  paymentMode = 'prepaid',
  overageCount,
}: {
  used: number;
  total: number;
  state: string;
  totalSuffix?: string;
  paymentMode?: 'prepaid' | 'postpaid';
  overageCount?: number;
}) => {
  const percent = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const restante = Math.max(0, total - used);
  const overage = overageCount ?? Math.max(0, used - total);
  const fill = FILL_CLASS[state] ?? 'bg-brand-600';
  const restanteColor =
    state === 'esgotado'
      ? 'text-error-700'
      : state === 'atencao'
        ? 'text-warning-700'
        : 'text-default-font';

  return (
    <>
      <div className="mb-2 flex items-baseline gap-1.5">
        <span className="text-default-font text-lg leading-none font-semibold">
          {formatNumber(used)}
        </span>
        <span className="text-xs text-neutral-500">
          / {formatNumber(total)}
          {totalSuffix ? ` ${totalSuffix}` : ''}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${percent}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        {state === 'esgotado' && paymentMode === 'postpaid' ? (
          <>
            <span className="text-default-font font-medium">
              {formatNumber(overage)} acima da franquia
            </span>
            <span className="text-neutral-500">vai na fatura</span>
          </>
        ) : state === 'esgotado' ? (
          <>
            <Badge variant="error" className="h-auto px-1.5 py-0 text-[10px]">
              Limite atingido
            </Badge>
            <span className="text-neutral-500">{percent}% usado</span>
          </>
        ) : (
          <>
            <span className={`font-medium ${restanteColor}`}>
              {formatNumber(restante)} restantes
            </span>
            <span className="text-neutral-500">{percent}% usado</span>
          </>
        )}
      </div>
    </>
  );
};

const CardShell = ({
  children,
  className,
  onClick,
  label,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  label?: string;
}) => {
  const baseClass = `bg-default-background flex w-full flex-col rounded-[16px] border border-solid border-neutral-200 p-3 ${className ?? ''}`;

  if (!onClick) return <div className={baseClass}>{children}</div>;

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`${baseClass} hover:border-brand-200 hover:bg-brand-50 text-left transition-colors`}
    >
      {children}
    </button>
  );
};

export const SaldoCicloCard: React.FC<SaldoCicloCardProps> = ({
  productName,
  planLabel,
  state,
  showLabel = true,
  used = 0,
  total = 0,
  primaryLabel,
  secondaryMetric,
  infoLabel,
  paymentMode = 'prepaid',
  overageCount,
  trialEndingSoon = false,
  onRenovar,
  onVerPlano,
  verPlanoLabel,
  className,
}) => {
  if (state === 'loading') {
    return (
      <CardShell className={className}>
        <Skeleton width="60%" height={12} className="mb-3" />
        <Skeleton width="100%" height={8} className="rounded-full" />
        <Skeleton width="45%" height={10} className="mt-3" />
      </CardShell>
    );
  }

  if (state === 'erro') {
    return (
      <CardShell className={className}>
        <p className="text-[11px] font-medium tracking-wide text-neutral-400 uppercase">
          Seu plano
        </p>
        <p className="text-error-600 mt-1 text-xs">Não foi possível carregar seu saldo.</p>
      </CardShell>
    );
  }

  if (state === 'semPlano') {
    return (
      <CardShell className={className}>
        <p className="text-default-font text-sm font-medium">Sem plano ativo</p>
        {infoLabel ? <p className="mt-0.5 text-xs text-neutral-500">{infoLabel}</p> : null}
        <Button variant="brand-primary" size="small" className="mt-3 w-full" onClick={onRenovar}>
          Renovar plano
        </Button>
      </CardShell>
    );
  }

  return (
    <CardShell
      className={className}
      onClick={onVerPlano}
      label={verPlanoLabel ?? `Ver o plano de ${productName}`}
    >
      {showLabel && (
        <p className="text-[11px] font-medium tracking-wide text-neutral-400 uppercase">
          Seu plano
        </p>
      )}
      <div className="mt-1 mb-2 flex items-center gap-2">
        <span className="text-default-font text-sm font-medium">
          {productName}
          {planLabel ? ` · ${planLabel}` : ''}
        </span>
        {(state === 'trial' || state === 'ilimitado') && (
          <Badge variant="brand" className="h-auto px-1.5 py-0 text-[10px]">
            Teste
          </Badge>
        )}
      </div>

      {primaryLabel && secondaryMetric ? (
        <p className="mb-1 text-[11px] font-medium text-neutral-500">{primaryLabel}</p>
      ) : null}

      {state === 'ilimitado' || state === 'semFranquia' ? (
        <>
          <div className="mb-2 flex items-baseline gap-1.5">
            <span className="text-default-font text-lg leading-none font-semibold">
              {formatNumber(used)}
            </span>
            <span className="text-xs text-neutral-500">usados</span>
          </div>
          <div className="flex items-center justify-between">
            <Badge variant="brand" className="h-auto px-1.5 py-0 text-[10px]">
              {state === 'ilimitado' ? 'Ilimitado no teste' : 'Cobrança por uso'}
            </Badge>
          </div>
        </>
      ) : (
        <SaldoBar
          used={used}
          total={total ?? 0}
          state={state === 'trial' && trialEndingSoon ? 'atencao' : state}
          totalSuffix={state === 'trial' ? 'no teste' : undefined}
          paymentMode={paymentMode}
          overageCount={overageCount}
        />
      )}

      {secondaryMetric ? (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <p className="mb-1 text-[11px] font-medium text-neutral-500">{secondaryMetric.label}</p>
          <SaldoBar
            used={secondaryMetric.used}
            total={secondaryMetric.total}
            state={secondaryMetric.state}
            paymentMode={secondaryMetric.paymentMode ?? paymentMode}
          />
        </div>
      ) : null}

      {infoLabel ? (
        <p
          className={`mt-2 text-xs ${trialEndingSoon ? 'text-warning-700 font-medium' : 'text-neutral-500'}`}
        >
          {infoLabel}
        </p>
      ) : null}
    </CardShell>
  );
};
